<template>
  <div :class="['exam-clock-container', themeClass]">
    <!-- Background animated blur blobs for premium glassmorphic feel -->
    <div class="glow-bg">
      <div class="glow-blob blob-1"></div>
      <div class="glow-blob blob-2"></div>
      <div class="glow-blob blob-3"></div>
    </div>

    <!-- Floating Top Bar for control actions (auto-hides when mouse is still) -->
    <div :class="['control-bar', { 'control-bar-hidden': isMouseIdle && isDisplayMode }]">
      <div class="control-left">
        <el-button class="glass-btn" circle @click="exitPage">
          <el-icon>
            <ArrowLeft />
          </el-icon>
        </el-button>
        <span class="page-title">监考时间大屏</span>
      </div>
      <div class="control-right">
        <el-button class="glass-btn" @click="toggleTheme" circle>
          <el-icon v-if="isDarkMode">
            <Sunny />
          </el-icon>
          <el-icon v-else>
            <Moon />
          </el-icon>
        </el-button>
        <el-button class="glass-btn" @click="toggleMute" circle>
          <el-icon v-if="isMuted">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
              <line x1="23" y1="9" x2="17" y2="15"></line>
              <line x1="17" y1="9" x2="23" y2="15"></line>
            </svg>
          </el-icon>
          <el-icon v-else>
            <Bell />
          </el-icon>
        </el-button>
        <el-button class="glass-btn" @click="toggleFullscreen" circle>
          <el-icon v-if="isFullscreen">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 14h6v6m10-6h-6v6M4 10h6V4m10 6h-6V4"></path>
            </svg>
          </el-icon>
          <el-icon v-else>
            <FullScreen />
          </el-icon>
        </el-button>
        <el-button type="primary" class="setup-btn" @click="openSetup">
          <el-icon>
            <Setting />
          </el-icon>
          <span>设置考务</span>
        </el-button>
      </div>
    </div>

    <!-- Main Display Section -->
    <div class="main-display">
      <el-row :gutter="40" class="display-row">
        <!-- Left Side: Time and Countdown -->
        <el-col :xs="24" :md="examForm.showInfoPanel !== false ? 14 : 24" class="col-left">
          <div :class="['glass-card', 'clock-card', { 'full-screen-mode': examForm.showInfoPanel === false }]">
            <!-- Current Real Time with Inline Status Tag -->
            <div class="current-time-section">
              <div class="time-header-row">
                <span class="label-text">当前标准时间</span>
                <span :class="['status-tag', statusClass]">{{ statusText }}</span>
              </div>
              <div class="real-time">{{ currentRealTime }}</div>
            </div>

            <!-- Visual Circle Progress (scaled up to fit larger countdown text) -->
            <div class="timer-progress-ring">
              <svg class="progress-ring-svg" viewBox="0 0 300 300" width="100%" height="100%">
                <circle class="progress-ring-bg" stroke="rgba(255,255,255,0.06)" stroke-width="14" fill="transparent"
                  r="130" cx="150" cy="150" />
                <circle class="progress-ring-bar" :stroke="progressColor" stroke-width="14" fill="transparent" r="130"
                  cx="150" cy="150" :stroke-dasharray="strokeDasharray" :stroke-dashoffset="strokeDashoffset"
                  stroke-linecap="round" />
              </svg>
              <div class="progress-ring-content">
                <span class="countdown-label">{{ countdownLabel }}</span>
                <span class="countdown-value">{{ countdownValue }}</span>
              </div>
            </div>

            <!-- Time Progress Details -->
            <div class="progress-detail">
              <div class="progress-bar-wrapper">
                <div class="progress-bar-fill"
                  :style="{ width: `${progressPercent}%`, backgroundColor: progressColor }"></div>
              </div>
              <div class="progress-text-row">
                <span>已进行 {{ elapsedMinutes }} / {{ examForm.duration }} 分钟</span>
                <span>进度 {{ progressPercent }}%</span>
              </div>
            </div>

            <!-- Exam Start & End markers -->
            <div class="time-markers">
              <div class="marker">
                <span class="marker-dot dot-start"></span>
                <span class="marker-label">开始：</span>
                <span class="marker-val">{{ examForm.startTime }}</span>
              </div>
              <div class="marker">
                <span class="marker-dot dot-end"></span>
                <span class="marker-label">结束：</span>
                <span class="marker-val">{{ computedEndTime }}</span>
              </div>
            </div>
          </div>
        </el-col>

        <!-- Right Side: Exam Metadata -->
        <el-col :xs="24" :md="10" v-if="examForm.showInfoPanel !== false" class="col-right">
          <div class="glass-card info-card">
            <!-- Close / Hide Button on top right of the card -->
            <button class="hide-panel-btn" @click="examForm.showInfoPanel = false" title="隐藏考务信息">
              <el-icon><Close /></el-icon>
            </button>


            <div class="info-grid">
              <!-- Subject -->
              <div class="info-item item-full">
                <div class="info-label">考试科目</div>
                <div class="info-val subject-text">{{ examForm.subject || '未设置' }}</div>
              </div>

              <!-- Room Name -->
              <div class="info-item item-full">
                <div class="info-label">考场名称</div>
                <div class="info-val room-text">{{ examForm.roomName || '未设置' }}</div>
              </div>

              <!-- Exam Time Range -->
              <div class="info-item">
                <div class="info-label">考试时间</div>
                <div class="info-val">{{ examForm.startTime }} - {{ computedEndTime }}</div>
              </div>

              <!-- Duration -->
              <div class="info-item">
                <div class="info-label">考试时长</div>
                <div class="info-val">{{ examForm.duration }} 分钟</div>
              </div>

              <!-- Pages count -->
              <div class="info-item">
                <div class="info-label">试卷页数</div>
                <div class="info-val highlight-val">{{ examForm.paperPages }} <span class="unit">页</span></div>
              </div>

              <!-- Answer sheet count -->
              <div class="info-item">
                <div class="info-label">答题卡页数</div>
                <div class="info-val highlight-val">{{ examForm.answerSheetPages }} <span class="unit">页</span></div>
              </div>

              <!-- Invigilators -->
              <div class="info-item item-full">
                <div class="info-label">监考人员</div>
                <div class="info-val invigilator-names">{{ examForm.invigilators || '暂无分配' }}</div>
              </div>
            </div>

            <!-- Rules & Notes -->
            <div class="rules-card">
              <div class="rules-header">
                <el-icon>
                  <InfoFilled />
                </el-icon>
                <span>考场注意事项</span>
              </div>
              <ul class="rules-list">
                <li v-for="(rule, idx) in activeRules" :key="idx">
                  {{ rule }}
                </li>
                <li v-if="activeRules.length === 0" class="no-rules">
                  未配置注意事项
                </li>
              </ul>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- Floating tab to restore info panel when hidden (auto-hides when mouse is idle) -->
    <transition name="slide-fade">
      <div v-if="!examForm.showInfoPanel" :class="['restore-panel-tab', { 'control-bar-hidden': isMouseIdle && isDisplayMode }]" @click="examForm.showInfoPanel = true" title="显示考务信息">
        <el-icon><ArrowLeft /></el-icon>
        <span class="tab-text">考务信息</span>
      </div>
    </transition>

    <!-- Apple Style Configuration Drawer/Dialog -->
    <el-dialog v-model="setupVisible" title="配置监考时间大屏" width="780px" custom-class="apple-settings-dialog"
      :close-on-click-modal="false" destroy-on-close>
      <div class="stateless-setup-container">
        <p class="picker-tip">🍎 选择或修改考务参数以配置监考时间大屏：</p>

        <el-form label-position="top">
          <el-row :gutter="24">
            <!-- Left Column: dropdown selections -->
            <el-col :span="12">
              <el-form-item label="考试科目">
                <el-select v-model="tempForm.subject" placeholder="选择科目" style="width: 100%">
                  <el-option v-for="sub in subjectOptions" :key="sub" :label="sub" :value="sub" />
                </el-select>
              </el-form-item>

              <el-form-item label="开始时间">
                <div style="display: flex; gap: 8px; align-items: center; width: 100%;">
                  <el-select v-model="tempForm.startHour" placeholder="时" style="flex: 1">
                    <el-option v-for="h in hoursList" :key="h" :label="h + ' 时'" :value="h" />
                  </el-select>
                  <span class="time-sep">:</span>
                  <el-select v-model="tempForm.startMinute" placeholder="分" style="flex: 1">
                    <el-option v-for="m in minutesList" :key="m" :label="m + ' 分'" :value="m" />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item label="考试时长">
                <el-select v-model="tempForm.duration" placeholder="选择时长" style="width: 100%">
                  <el-option v-for="d in durationsList" :key="d" :label="d + ' 分钟'" :value="d" />
                </el-select>
              </el-form-item>

              <el-row :gutter="10">
                <el-col :span="12">
                  <el-form-item label="试卷页数">
                    <el-select v-model="tempForm.paperPages" placeholder="选择页数" style="width: 100%">
                      <el-option v-for="p in pagesList" :key="p" :label="p + ' 页'" :value="p" />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="答题卡页数">
                    <el-select v-model="tempForm.answerSheetPages" placeholder="选择页数" style="width: 100%">
                      <el-option v-for="ap in answerSheetPagesList" :key="ap" :label="ap + ' 页'" :value="ap" />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-col>

            <!-- Right Column: input parameters -->
            <el-col :span="12">
              <el-form-item label="考场名称">
                <el-input v-model="tempForm.roomName" placeholder="如：第一考场、101考场" :prefix-icon="Location" />
              </el-form-item>

              <el-form-item label="监考教师">
                <el-input v-model="tempForm.invigilators" placeholder="请输入监考教师姓名（空格隔开）" :prefix-icon="User" />
              </el-form-item>

              <el-form-item label="显示选项">
                <el-checkbox v-model="tempForm.showInfoPanel" style="margin-left: 0;">显示右侧考务信息面板</el-checkbox>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>

        <!-- Checkbox settings -->
        <div class="common-setup-section" style="margin-top: 15px;">
          <h4 class="section-title">💡 考场注意事项 (可勾选显示)</h4>
          <div class="rules-checkbox-grid">
            <el-checkbox v-for="(rule, idx) in presetRules" :key="idx" v-model="rule.enabled">
              {{ rule.text }}
            </el-checkbox>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="setupVisible = false">取消</el-button>
          <el-button type="primary" size="large" @click="saveAndStart" class="confirm-start-btn">
            <el-icon>
              <Check />
            </el-icon>
            开始监考展示
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  ArrowLeft, Sunny, Moon, Bell, FullScreen, Setting, InfoFilled, Check, Location, User, Close
} from '@element-plus/icons-vue'
import { isLoggedIn } from '@/utils/auth'

const router = useRouter()

const isUserLoggedIn = computed(() => isLoggedIn())

// UI state
const isDarkMode = ref(true)
const isMuted = ref(false)
const isFullscreen = ref(false)
const setupVisible = ref(true)
const isDisplayMode = ref(false)
const isMouseIdle = ref(false)
let mouseTimer = null

// Real time clock
const currentRealTime = ref('')
let clockInterval = null

// Exam states
const examForm = ref({
  subject: '语文',
  startTime: '09:00',
  duration: 120,
  paperPages: 4,
  answerSheetPages: 2,
  invigilators: '',
  roomName: '第一考场',
  showInfoPanel: true
})

// Temp states during edit
const tempForm = ref({
  subject: '语文',
  startHour: '09',
  startMinute: '00',
  duration: 120,
  paperPages: 4,
  answerSheetPages: 2,
  invigilators: '',
  roomName: '第一考场',
  showInfoPanel: true
})

// Rules selection
const presetRules = ref([
  { text: '保持考场安静，遵守诚信考试守则。', enabled: true },
  { text: '答题卡填涂一律使用2B铅笔，书写使用0.5mm黑色中性笔。', enabled: true },
  { text: '严禁携带手机、智能手环等通讯工具及任何电子设备进入考场。', enabled: true },
  { text: '考试结束前15分钟不得提前交卷离场。', enabled: true },
  { text: '请认真核对条形码信息并贴在答题卡指定位置。', enabled: true },
  { text: '考试开始15分钟后迟到考生不得进入考场。', enabled: false },
  { text: '闭卷考试，禁止交头接耳，发现作弊行为取消考试成绩。', enabled: false }
])

const activeRules = computed(() => {
  return presetRules.value.filter(r => r.enabled).map(r => r.text)
})

// Options list for wheel pickers
const subjectOptions = [
  '语文', '数学', '英语', '物理', '化学', '生物', '历史', '地理', '政治',
  '科学', '信息技术', '通用技术', '体育', '美术', '音乐', '班会', '自习'
]
const hoursList = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
const minutesList = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'))
const durationsList = [30, 40, 45, 50, 60, 75, 80, 90, 100, 105, 120, 135, 150, 180, 210, 240]
const pagesList = Array.from({ length: 20 }, (_, i) => i + 1)
const answerSheetPagesList = Array.from({ length: 11 }, (_, i) => i)

// 3D Progress ring configurations (radius changed to 130 to fit larger text)
const strokeDasharray = 2 * Math.PI * 130
const strokeDashoffset = computed(() => {
  const percent = progressPercent.value
  return strokeDasharray - (percent / 100) * strokeDasharray
})

// Computed values
const themeClass = computed(() => isDarkMode.value ? 'dark-theme' : 'light-theme')

// Format target start time as Date
const parsedStartTime = computed(() => {
  const [h, m] = examForm.value.startTime.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m, 0, 0)
  return d
})

// Compute End time string
const computedEndTime = computed(() => {
  const start = parsedStartTime.value
  const end = new Date(start.getTime() + examForm.value.duration * 60 * 1000)
  return `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`
})

// Live calculations for countdown and elapsed
const nowTime = ref(new Date())

// Tick clock every second
const tickRealTime = () => {
  const now = new Date()
  nowTime.value = now

  // Format current standard time
  const hh = now.getHours().toString().padStart(2, '0')
  const mm = now.getMinutes().toString().padStart(2, '0')
  const ss = now.getSeconds().toString().padStart(2, '0')
  currentRealTime.value = `${hh}:${mm}:${ss}`
}

const elapsedMinutes = computed(() => {
  const start = parsedStartTime.value
  const now = nowTime.value
  if (now < start) return 0

  const diffMs = now.getTime() - start.getTime()
  const mins = Math.floor(diffMs / (60 * 1000))
  return Math.min(examForm.value.duration, mins)
})

const remainingSeconds = computed(() => {
  const start = parsedStartTime.value
  const end = new Date(start.getTime() + examForm.value.duration * 60 * 1000)
  const now = nowTime.value

  if (now < start) {
    // Before start
    return Math.floor((start.getTime() - now.getTime()) / 1000)
  } else if (now > end) {
    // Ended
    return 0
  } else {
    // In progress
    return Math.floor((end.getTime() - now.getTime()) / 1000)
  }
})

const isExamStarted = computed(() => {
  return nowTime.value >= parsedStartTime.value
})

const isExamEnded = computed(() => {
  const start = parsedStartTime.value
  const end = new Date(start.getTime() + examForm.value.duration * 60 * 1000)
  return nowTime.value >= end
})

const progressPercent = computed(() => {
  if (!isExamStarted.value) return 0
  if (isExamEnded.value) return 100
  const elapsed = nowTime.value.getTime() - parsedStartTime.value.getTime()
  const total = examForm.value.duration * 60 * 1000
  return Math.floor((elapsed / total) * 100)
})

const progressColor = computed(() => {
  if (isExamEnded.value) return '#ef4444' // Red
  if (remainingSeconds.value <= 15 * 60) {
    // Last 15 minutes - Warning color
    return '#f97316' // Orange
  }
  return '#10b981' // Green
})

const statusText = computed(() => {
  if (isExamEnded.value) return '已 结 束'
  if (!isExamStarted.value) return '未 开 始'
  if (remainingSeconds.value <= 15 * 60) return '即将结束'
  return '考 试 中'
})

const statusClass = computed(() => {
  if (isExamEnded.value) return 'status-ended'
  if (!isExamStarted.value) return 'status-pending'
  if (remainingSeconds.value <= 15 * 60) return 'status-warning'
  return 'status-running'
})

const countdownLabel = computed(() => {
  if (!isExamStarted.value) return '距离考试开始'
  if (isExamEnded.value) return '考试已结束'
  return '距离考试结束'
})

const countdownValue = computed(() => {
  const totalSecs = remainingSeconds.value
  if (totalSecs === 0 && isExamEnded.value) {
    return '00:00:00'
  }
  const hh = Math.floor(totalSecs / 3600).toString().padStart(2, '0')
  const mm = Math.floor((totalSecs % 3600) / 60).toString().padStart(2, '0')
  const ss = (totalSecs % 60).toString().padStart(2, '0')
  return `${hh}:${mm}:${ss}`
})

// Trigger bells at key events (starts, 15m left, end)
let startBellTriggered = false
let warningBellTriggered = false
let endBellTriggered = false

watch(remainingSeconds, (newSecs) => {
  if (isMuted.value || !isDisplayMode.value) return

  // 1. Exam Starts
  if (isExamStarted.value && !isExamEnded.value && !startBellTriggered) {
    startBellTriggered = true
    playBellSound()
    ElMessage.success('考试正式开始，铃声已响')
  }

  // 2. 15 Minutes Left
  if (isExamStarted.value && newSecs <= 15 * 60 && newSecs > 14 * 60 + 50 && !warningBellTriggered) {
    warningBellTriggered = true
    playBellSound()
    ElMessage.warning('距离考试结束还有15分钟！提示铃声已响')
  }

  // 3. Exam Ends
  if (isExamEnded.value && !endBellTriggered) {
    endBellTriggered = true
    playBellSound()
    ElMessage.error('考试时间到，请立即停止答卷！终考铃声已响')
  }
})

// Synthesize pleasant dual-tone school bell sound using Web Audio API
const playBellSound = () => {
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)()

    const playTone = (freq, duration, startTime) => {
      const osc = audioCtx.createOscillator()
      const gain = audioCtx.createGain()

      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, startTime)

      gain.gain.setValueAtTime(0.5, startTime)
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration)

      osc.connect(gain)
      gain.connect(audioCtx.destination)

      osc.start(startTime)
      osc.stop(startTime + duration)
    }

    // Play Westminster Chimes short style (C#5 followed by A4)
    const now = audioCtx.currentTime
    playTone(554.37, 1.0, now) // C#5
    playTone(440.00, 1.4, now + 0.5) // A4
  } catch (e) {
    console.error('Failed to play synthesized sound:', e)
  }
}

// UI triggers
const toggleTheme = () => {
  isDarkMode.value = !isDarkMode.value
}

const toggleMute = () => {
  isMuted.value = !isMuted.value
  ElMessage.info(isMuted.value ? '已静音考场广播铃声' : '已开启考场广播铃声')
}

const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().then(() => {
      isFullscreen.value = true
    }).catch(err => {
      ElMessage.error(`全屏失败: ${err.message}`)
    })
  } else {
    document.exitFullscreen().then(() => {
      isFullscreen.value = false
    })
  }
}

const exitPage = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen()
  }
  if (isUserLoggedIn.value) {
    router.push('/exams')
  } else {
    router.push('/portal')
  }
}

const openSetup = () => {
  setupVisible.value = true
}

// Sync tempForm with current values when opening setup
watch(setupVisible, (val) => {
  if (val) {
    const [h, m] = examForm.value.startTime.split(':')
    tempForm.value = {
      subject: examForm.value.subject,
      startHour: h || '09',
      startMinute: m || '00',
      duration: examForm.value.duration,
      paperPages: examForm.value.paperPages,
      answerSheetPages: examForm.value.answerSheetPages,
      invigilators: examForm.value.invigilators,
      roomName: examForm.value.roomName,
      showInfoPanel: examForm.value.showInfoPanel !== false
    }
  }
})

// Save settings and start displaying
const saveAndStart = () => {
  examForm.value.subject = tempForm.value.subject
  examForm.value.startTime = `${tempForm.value.startHour}:${tempForm.value.startMinute}`
  examForm.value.duration = tempForm.value.duration
  examForm.value.paperPages = tempForm.value.paperPages
  examForm.value.answerSheetPages = tempForm.value.answerSheetPages
  examForm.value.invigilators = tempForm.value.invigilators
  examForm.value.roomName = tempForm.value.roomName
  examForm.value.showInfoPanel = tempForm.value.showInfoPanel

  // Reset triggers for bells
  startBellTriggered = false
  warningBellTriggered = false
  endBellTriggered = false

  setupVisible.value = false
  isDisplayMode.value = true
  ElMessage.success('考务设置应用成功')
}

// Mouse idle detection in display mode
const handleMouseMove = () => {
  isMouseIdle.value = false
  clearTimeout(mouseTimer)
  if (isDisplayMode.value) {
    mouseTimer = setTimeout(() => {
      isMouseIdle.value = true
    }, 3000) // hide after 3 seconds of inactivity
  }
}

// Fullscreen state listener
const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement
}

onMounted(() => {
  tickRealTime()
  clockInterval = setInterval(tickRealTime, 1000)

  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('fullscreenchange', handleFullscreenChange)
})

onUnmounted(() => {
  clearInterval(clockInterval)
  clearTimeout(mouseTimer)
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('fullscreenchange', handleFullscreenChange)
})
</script>

<style scoped>
/* Main Container styling */
.exam-clock-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow-x: hidden;
  transition: background-color 0.5s ease, color 0.5s ease;
}

/* Background animated blur blobs */
.glow-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.glow-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(140px);
  opacity: 0.25;
  transition: all 1s ease;
}

/* Themes: Dark Theme */
.dark-theme {
  background-color: #0f111a;
  color: #f3f4f6;
}

.dark-theme .glow-blob.blob-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.4) 0%, rgba(99, 102, 241, 0) 70%);
  top: -200px;
  left: -200px;
}

.dark-theme .glow-blob.blob-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0) 70%);
  bottom: -100px;
  right: -100px;
}

.dark-theme .glow-blob.blob-3 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(236, 72, 153, 0.25) 0%, rgba(236, 72, 153, 0) 70%);
  top: 30%;
  right: 20%;
}

/* Themes: Light Theme */
.light-theme {
  background-color: #f6f8fb;
  color: #1f2937;
}

.light-theme .glow-blob.blob-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(199, 210, 254, 0.5) 0%, rgba(199, 210, 254, 0) 70%);
  top: -200px;
  left: -200px;
}

.light-theme .glow-blob.blob-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(167, 243, 208, 0.4) 0%, rgba(167, 243, 208, 0) 70%);
  bottom: -100px;
  right: -100px;
}

.light-theme .glow-blob.blob-3 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(253, 164, 175, 0.35) 0%, rgba(253, 164, 175, 0) 70%);
  top: 30%;
  right: 20%;
}

/* Control Bar styling */
.control-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 30px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
}

.control-bar-hidden {
  transform: translateY(-100%);
  opacity: 0;
  pointer-events: none;
}

.control-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.control-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.glass-btn {
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: inherit !important;
  transition: all 0.3s ease;
}

.glass-btn:hover {
  background: rgba(255, 255, 255, 0.18) !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
  transform: scale(1.05);
}

.light-theme .glass-btn {
  background: rgba(0, 0, 0, 0.04) !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.light-theme .glass-btn:hover {
  background: rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}

.setup-btn {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%) !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  border-radius: 20px !important;
  padding: 8px 18px !important;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
  transition: all 0.3s ease;
}

.setup-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
}

/* Main Display Section */
.main-display {
  flex: 1;
  z-index: 10;
  display: flex;
  padding: 80px 40px 30px 40px;
  align-items: stretch;
  box-sizing: border-box;
  height: 100vh;
  overflow: hidden;
}

.display-row {
  width: 100%;
  height: 100%;
  margin: 0 !important;
}

.col-left,
.col-right {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Glass Card common styling */
.glass-card {
  height: 100%;
  max-height: 100%;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  padding: 16px 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.light-theme .glass-card {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

/* Clock Card Styling */
.clock-card {
  position: relative;
  align-items: center;
  justify-content: center;
  gap: min(20px, 2.5vh);
  margin: 0 auto;
  max-width: min(620px, 55vw);
  width: 100%;
}

.status-tag-wrapper {
  margin-bottom: 5px;
}

.status-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 750;
  letter-spacing: 1px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  animation: pulse 2s infinite alternate ease-in-out;
}

.time-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 2px;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.95;
  }

  100% {
    transform: scale(1.04);
    opacity: 1;
  }
}

.status-pending {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.status-running {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-warning {
  background: rgba(249, 115, 22, 0.15);
  color: #f97316;
  border: 1px solid rgba(249, 115, 22, 0.3);
}

.status-ended {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.current-time-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
}

.current-time-section .label-text {
  font-size: 15px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 2px;
}

.light-theme .current-time-section .label-text {
  color: #6b7280;
}

.real-time {
  font-size: min(88px, 11vh, 8vw);
  font-weight: 850;
  letter-spacing: -1.5px;
  line-height: 1;
  background: linear-gradient(180deg, #ffffff 0%, #d1d5db 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  font-variant-numeric: tabular-nums;
}

.light-theme .real-time {
  background: linear-gradient(180deg, #1f2937 0%, #4b5563 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Timer Circle Progress styling */
.timer-progress-ring {
  position: relative;
  width: min(300px, 32vh, 22vw);
  height: min(300px, 32vh, 22vw);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 6px 0;
  flex-shrink: 0;
}

.progress-ring-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  /* Start from top */
}

.progress-ring-bar {
  transition: stroke-dashoffset 1s linear, stroke 0.5s ease;
}

.progress-ring-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 10;
}

.countdown-label {
  font-size: 16px;
  color: #9ca3af;
  margin-bottom: 6px;
  font-weight: 500;
}

.light-theme .countdown-label {
  color: #6b7280;
}

.countdown-value {
  font-size: min(54px, 7vh, 5vw);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.5px;
  line-height: 1;
}

/* Time progress text bar */
.progress-detail {
  width: 100%;
  max-width: 420px;
  margin-top: 10px;
}

.progress-bar-wrapper {
  width: 100%;
  height: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
  border: 1px solid rgba(255, 255, 255, 0.02);
}

.light-theme .progress-bar-wrapper {
  background: rgba(0, 0, 0, 0.05);
}

.progress-bar-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 1s linear, background-color 0.5s ease;
}

.progress-text-row {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  color: #9ca3af;
  font-weight: 500;
}

.light-theme .progress-text-row {
  color: #4b5563;
}

/* Time markers */
.time-markers {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 500px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
}

.light-theme .time-markers {
  border-top-color: rgba(0, 0, 0, 0.08);
}

.marker {
  display: flex;
  align-items: center;
  font-size: 20px;
}

.marker-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

.dot-start {
  background-color: #3b82f6;
  box-shadow: 0 0 8px rgba(59, 130, 246, 0.6);
}

.dot-end {
  background-color: #ef4444;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.6);
}

.marker-label {
  color: #9ca3af;
  font-weight: 500;
}

.light-theme .marker-label {
  color: #4b5563;
}

.marker-val {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Info Card (Right Side) Styling */
.info-card {
  align-items: stretch;
  justify-content: flex-start;
  gap: min(20px, 2.5vh);
  margin: 0 auto;
  max-width: min(480px, 40vw);
  width: 100%;
}

.info-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding-bottom: 15px;
  margin-bottom: 10px;
}

.light-theme .info-header {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

.info-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.02);
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.03);
}

.light-theme .info-item {
  background: rgba(0, 0, 0, 0.01);
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.item-full {
  grid-column: span 2;
}

.info-label {
  font-size: min(14px, 1.8vh, 1.3vw);
  color: #9ca3af;
  margin-bottom: 4px;
  font-weight: 500;
}

.light-theme .info-label {
  color: #6b7280;
}

.info-val {
  font-size: min(20px, 2.5vh, 1.8vw);
  font-weight: 700;
}

.subject-text {
  font-size: min(28px, 3.5vh, 2.5vw);
  font-weight: 800;
  color: #4f46e5;
}

.light-theme .subject-text {
  color: #3b82f6;
}

.invigilator-names {
  font-size: min(18px, 2.3vh, 1.6vw);
  font-weight: 600;
  color: inherit;
  line-height: 1.4;
}

.highlight-val {
  font-size: min(24px, 3vh, 2.2vw);
  color: #10b981;
}

.light-theme .highlight-val {
  color: #059669;
}

.highlight-val .unit {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 500;
  margin-left: 2px;
}

.light-theme .highlight-val .unit {
  color: #6b7280;
}

/* Attendance Card Styling */
.attendance-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.light-theme .attendance-card {
  background: rgba(0, 0, 0, 0.015);
  border-color: rgba(0, 0, 0, 0.04);
}

.att-header {
  font-size: 14px;
  font-weight: 700;
  color: #9ca3af;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.06);
  padding-bottom: 6px;
}

.light-theme .att-header {
  color: #4b5563;
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

.att-counters {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.counter-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.counter-label {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

.light-theme .counter-label {
  color: #6b7280;
}

.counter-control {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
}

.light-theme .counter-control {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.05);
}

.cnt-btn {
  background: transparent;
  border: none;
  color: inherit;
  width: 28px;
  height: 28px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.cnt-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.light-theme .cnt-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.cnt-val {
  padding: 0 10px;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.missing-val {
  color: #ef4444;
  font-size: 16px;
}

.missing-ids-section {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
}

.missing-ids-label {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  font-weight: 500;
}

.light-theme .missing-ids-label {
  color: #6b7280;
}

/* Custom styles for inputs to blend in glass theme */
.glass-input :deep(.el-input__wrapper) {
  background-color: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08) inset !important;
  border: none !important;
}

.glass-input :deep(.el-input__inner) {
  color: inherit !important;
  font-size: 12px;
}

.light-theme .glass-input :deep(.el-input__wrapper) {
  background-color: rgba(255, 255, 255, 0.8) !important;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08) inset !important;
}

/* Rules Section styling */
.rules-card {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rules-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: min(16px, 2.2vh, 1.5vw);
  font-weight: 700;
  color: #9ca3af;
}

.light-theme .rules-header {
  color: #4b5563;
}

.rules-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 80px;
  overflow-y: auto;
}

.rules-list li {
  font-size: min(15px, 2vh, 1.4vw);
  line-height: 1.4;
  color: #d1d5db;
  padding-left: 12px;
  position: relative;
  font-weight: 500;
}

.light-theme .rules-list li {
  color: #374151;
}

.rules-list li::before {
  content: "•";
  color: #3b82f6;
  position: absolute;
  left: 0;
  top: 0;
  font-weight: bold;
}

.no-rules {
  color: #9ca3af !important;
  font-style: italic;
  padding-left: 0 !important;
}

.no-rules::before {
  display: none !important;
}

/* Apple Style Dialog overrides */
:deep(.apple-settings-dialog) {
  background: rgba(30, 32, 45, 0.85) !important;
  backdrop-filter: blur(25px) !important;
  -webkit-backdrop-filter: blur(25px) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 24px !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3) !important;
}

.light-theme :deep(.apple-settings-dialog) {
  background: rgba(255, 255, 255, 0.9) !important;
  border-color: rgba(0, 0, 0, 0.1) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15) !important;
}

:deep(.apple-settings-dialog .el-dialog__title) {
  color: #ffffff !important;
  font-weight: 750 !important;
  font-size: 20px !important;
}

.light-theme :deep(.apple-settings-dialog .el-dialog__title) {
  color: #111827 !important;
}

:deep(.apple-settings-dialog .el-dialog__body) {
  padding-top: 10px !important;
}

.setup-tabs :deep(.el-tabs__item) {
  color: rgba(255, 255, 255, 0.5) !important;
  font-weight: 600;
  font-size: 15px;
}

.setup-tabs :deep(.el-tabs__item.is-active) {
  color: #3b82f6 !important;
}

.light-theme .setup-tabs :deep(.el-tabs__item) {
  color: #6b7280 !important;
}

.light-theme .setup-tabs :deep(.el-tabs__item.is-active) {
  color: #4f46e5 !important;
}

/* Loaded system info display box */
.loaded-info-box {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: 16px;
  margin-top: 20px;
}

.loaded-title {
  font-size: 13.5px;
  font-weight: bold;
  color: #60a5fa;
  margin-bottom: 8px;
}

.loaded-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  font-size: 13px;
  color: #e5e7eb;
}

.light-theme .loaded-grid {
  color: #374151;
}

/* Custom pickers arrangement (Apple Cylinder wheels side-by-side) */
.custom-pickers-section {
  padding: 10px 0;
}

.picker-tip {
  font-size: 13px;
  color: #9ca3af;
  margin-bottom: 15px;
}

.light-theme .picker-tip {
  color: #6b7280;
}

.wheel-pickers-container {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 18px;
  padding: 15px 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.light-theme .wheel-pickers-container {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.05);
}

.picker-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.picker-title {
  font-size: 12px;
  color: #9ca3af;
  font-weight: bold;
}

.light-theme .picker-title {
  color: #4b5563;
}

/* Customize IosWheelPicker class within dialog */
:deep(.dark-picker-text) .ios-picker-item.active {
  color: #ffffff !important;
}

:deep(.dark-picker-text) .ios-picker-item {
  color: rgba(255, 255, 255, 0.4) !important;
}

.light-theme :deep(.dark-picker-text) .ios-picker-item.active {
  color: #111827 !important;
}

.light-theme :deep(.dark-picker-text) .ios-picker-item {
  color: rgba(0, 0, 0, 0.4) !important;
}

.light-theme :deep(.dark-picker-text) .ios-picker-highlight {
  border-top-color: rgba(0, 0, 0, 0.1);
  border-bottom-color: rgba(0, 0, 0, 0.1);
  background: rgba(0, 0, 0, 0.02);
}

/* Common Setup */
.common-setup-section {
  margin-top: 25px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 20px;
}

.light-theme .common-setup-section {
  border-top-color: rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.rules-checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 20px;
}

.rules-checkbox-grid :deep(.el-checkbox) {
  color: #9ca3af !important;
  white-space: normal;
  display: flex;
  align-items: flex-start;
  height: auto;
  line-height: 1.4;
}

.light-theme .rules-checkbox-grid :deep(.el-checkbox) {
  color: #4b5563 !important;
}

.rules-checkbox-grid :deep(.el-checkbox__label) {
  font-size: 12px;
  padding-left: 8px;
}

.rules-checkbox-grid :deep(.el-checkbox__input) {
  margin-top: 2px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.confirm-start-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  color: white !important;
  font-weight: 700 !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25) !important;
}

.confirm-start-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4) !important;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .display-row {
    flex-direction: column;
    gap: 30px;
  }

  .col-left,
  .col-right {
    max-width: 100% !important;
    width: 100% !important;
  }



  .wheel-pickers-container {
    grid-template-columns: repeat(3, 1fr);
  }

  .rules-checkbox-grid {
    grid-template-columns: 1fr;
  }
}

/* Stateless setup layout styling */
.stateless-setup-container {
  padding: 5px 0;
}

.picker-tip {
  font-size: 13.5px;
  color: #a8abb2;
  margin-bottom: 20px;
}

.light-theme .picker-tip {
  color: #4b5563;
}

.setup-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 24px;
}

@media (max-width: 768px) {
  .setup-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.wheels-container {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
}

.light-theme .wheels-container {
  background: rgba(0, 0, 0, 0.015);
  border-color: rgba(0, 0, 0, 0.06);
}

.wheel-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.picker-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.picker-title {
  font-size: 12px;
  font-weight: 600;
  color: #8a8d93;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.light-theme .picker-title {
  color: #4b5563;
}

.inputs-container {
  display: flex;
  flex-direction: column;
}

.room-text {
  font-size: 24px;
  font-weight: 700;
  color: #3b82f6;
}

.light-theme .room-text {
  color: #4f46e5;
}

/* Clock Card Full Screen Mode Styling */
.clock-card.full-screen-mode {
  max-width: 820px;
  padding: 30px 48px;
  gap: min(25px, 3.2vh);
}

.clock-card.full-screen-mode .real-time {
  font-size: min(110px, 14vh) !important;
}

.clock-card.full-screen-mode .timer-progress-ring {
  width: min(360px, 40vh) !important;
  height: min(360px, 40vh) !important;
}

.clock-card.full-screen-mode .countdown-value {
  font-size: min(68px, 9vh) !important;
}

.clock-card.full-screen-mode .countdown-label {
  font-size: 18px !important;
}

.clock-card.full-screen-mode .current-time-section .label-text {
  font-size: 18px !important;
}

.clock-card.full-screen-mode .status-tag {
  font-size: 16px !important;
  padding: 5px 14px !important;
}

.clock-card.full-screen-mode .progress-detail {
  max-width: 580px;
  width: 100%;
}

.clock-card.full-screen-mode .progress-bar-wrapper {
  height: 16px;
  border-radius: 8px;
}

.clock-card.full-screen-mode .progress-bar-fill {
  border-radius: 8px;
}

.clock-card.full-screen-mode .progress-text-row {
  font-size: 18px;
}

.clock-card.full-screen-mode .time-markers {
  max-width: 580px;
}

.clock-card.full-screen-mode .marker {
  font-size: 24px;
}

.clock-card.full-screen-mode .marker-dot {
  width: 14px;
  height: 14px;
}

.time-sep {
  font-size: 18px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.7);
}

.light-theme .time-sep {
  color: #606266;
}

/* Beautiful On-Screen Panel Toggle Styles */
.hide-panel-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  outline: none;
  z-index: 10;
}

.hide-panel-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.25);
  color: #ffffff;
  transform: scale(1.1);
}

.light-theme .hide-panel-btn {
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #4b5563;
}

.light-theme .hide-panel-btn:hover {
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.15);
  color: #111827;
}

.restore-panel-tab {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 99;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-right: none;
  border-radius: 16px 0 0 16px;
  padding: 20px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
}

.restore-panel-tab:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
  padding-right: 15px; /* Pull out slightly on hover */
}

.restore-panel-tab .tab-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 13px;
  font-weight: 700;
  color: #d1d5db;
  letter-spacing: 2px;
}

.restore-panel-tab .el-icon {
  font-size: 14px;
  color: #d1d5db;
  transition: transform 0.3s ease;
}

.restore-panel-tab:hover .el-icon {
  transform: translateX(-2px);
}

.light-theme .restore-panel-tab {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-right: none;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.05);
}

.light-theme .restore-panel-tab .tab-text,
.light-theme .restore-panel-tab .el-icon {
  color: #374151;
}

.light-theme .restore-panel-tab:hover {
  background: rgba(255, 255, 255, 0.95);
}

/* Slide-Fade transition for floating tab */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translate(100%, -50%);
  opacity: 0;
}
</style>

<style>
/* Global resets to ensure the page never scrolls or displays scrollbars on any viewport */
html, body {
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
  height: 100% !important;
  width: 100% !important;
}
</style>
